// ============================================
// Manor Miser - Layout Constants
// ============================================
// Adaptive layout: landscape (board left, HUD right) or
// portrait (board top, HUD below). Phaser.Scale.FIT upscales.

// Tile / board
export const TILE_SIZE = 16;
export const MAP_TILES = 12;
export const BOARD_SIZE = MAP_TILES * TILE_SIZE; // 192

// Orientation
export type Orientation = 'landscape' | 'portrait';

// Canvas dimensions per orientation
const LANDSCAPE_W = 272;
const LANDSCAPE_H = 192;
const PORTRAIT_W = 192;
const PORTRAIT_H = 280;

export function getOrientation(): Orientation {
  return window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait';
}

export function getCanvasSize(orientation: Orientation): { width: number; height: number } {
  return orientation === 'landscape'
    ? { width: LANDSCAPE_W, height: LANDSCAPE_H }
    : { width: PORTRAIT_W, height: PORTRAIT_H };
}

// Player grid positions in tile coordinates (within the 12×12 tilemap)
export const PLAYER_TILE_POSITIONS = [
  { col: 3, row: 7 }, { col: 5, row: 7 }, { col: 7, row: 7 }, { col: 9, row: 7 },
  { col: 3, row: 9 }, { col: 5, row: 9 }, { col: 7, row: 9 }, { col: 9, row: 9 },
];

/** Convert tile coordinates to screen coordinates (native resolution, no scaling) */
export function tileToScreen(col: number, row: number): { x: number; y: number } {
  return {
    x: col * TILE_SIZE + TILE_SIZE / 2,
    y: row * TILE_SIZE + TILE_SIZE / 2,
  };
}

// ----------------------------------------
// HUD layout — computed per orientation
// ----------------------------------------

export interface HUDLayout {
  orientation: Orientation;
  // HUD panel rectangle
  panelX: number;
  panelY: number;
  panelW: number;
  panelH: number;
  // Divider line (separates board from HUD)
  dividerStart: { x: number; y: number };
  dividerEnd: { x: number; y: number };
  // HUD element positions
  roundText:   { x: number; y: number };
  timerText:   { x: number; y: number };
  progressBar: { x: number; y: number; w: number; h: number };
  phaseText:   { x: number; y: number };
  divider1:    { x1: number; y1: number; x2: number; y2: number };
  coinsLabel:  { x: number; y: number };
  coinsValue:  { x: number; y: number };
  betLabel:    { x: number; y: number };
  betValue:    { x: number; y: number };
  divider2:    { x1: number; y1: number; x2: number; y2: number };
  betBtn1:     { x: number; y: number; w: number; h: number };
  betBtn2:     { x: number; y: number; w: number; h: number };
  betBtn3:     { x: number; y: number; w: number; h: number };
  soundToggle: { x: number; y: number };
  spectator:   { x: number; y: number };
}

export function getLayout(orientation: Orientation): HUDLayout {
  if (orientation === 'landscape') {
    return getLandscapeLayout();
  }
  return getPortraitLayout();
}

// ----------------------------------------
// Landscape: board left (0,0 192×192), HUD right (192,0 80×192)
// ----------------------------------------
function getLandscapeLayout(): HUDLayout {
  const hx = BOARD_SIZE;        // 192
  const hw = LANDSCAPE_W - BOARD_SIZE; // 80
  const cx = hx + hw / 2;       // 232

  return {
    orientation: 'landscape',
    panelX: hx, panelY: 0, panelW: hw, panelH: BOARD_SIZE,
    dividerStart: { x: hx, y: 0 },
    dividerEnd:   { x: hx, y: BOARD_SIZE },
    roundText:    { x: cx, y: 4 },
    timerText:    { x: cx, y: 14 },
    progressBar:  { x: cx, y: 30, w: 60, h: 4 },
    phaseText:    { x: cx, y: 34 },
    divider1:     { x1: hx + 4, y1: 48, x2: hx + hw - 4, y2: 48 },
    coinsLabel:   { x: cx, y: 52 },
    coinsValue:   { x: cx, y: 60 },
    betLabel:     { x: cx, y: 88 },
    betValue:     { x: cx, y: 96 },
    divider2:     { x1: hx + 4, y1: 124, x2: hx + hw - 4, y2: 124 },
    betBtn1:      { x: cx, y: 134, w: 60, h: 14 },
    betBtn2:      { x: cx, y: 152, w: 60, h: 14 },
    betBtn3:      { x: cx, y: 170, w: 60, h: 14 },
    soundToggle:  { x: hx + hw - 6, y: 4 },
    spectator:    { x: cx, y: 186 },
  };
}

// ----------------------------------------
// Portrait: board top (0,0 192×192), HUD below (0,192 192×72)
// ----------------------------------------
function getPortraitLayout(): HUDLayout {
  const hy = BOARD_SIZE;         // 192
  const hh = PORTRAIT_H - BOARD_SIZE; // 72
  const cx = PORTRAIT_W / 2;    // 96
  const lx = PORTRAIT_W / 4;    // 48 — left quarter
  const rx = PORTRAIT_W * 3 / 4; // 144 — right quarter

  return {
    orientation: 'portrait',
    panelX: 0, panelY: hy, panelW: PORTRAIT_W, panelH: hh,
    dividerStart: { x: 0, y: hy },
    dividerEnd:   { x: PORTRAIT_W, y: hy },
    roundText:    { x: 30, y: hy + 4 },
    timerText:    { x: cx, y: hy + 2 },
    progressBar:  { x: cx, y: hy + 16, w: 160, h: 4 },
    phaseText:    { x: 162, y: hy + 4 },
    divider1:     { x1: 4, y1: hy + 20, x2: PORTRAIT_W - 4, y2: hy + 20 },
    coinsLabel:   { x: lx, y: hy + 24 },
    coinsValue:   { x: lx, y: hy + 32 },
    betLabel:     { x: rx, y: hy + 24 },
    betValue:     { x: rx, y: hy + 32 },
    divider2:     { x1: 4, y1: hy + 58, x2: PORTRAIT_W - 4, y2: hy + 58 },
    betBtn1:      { x: 36, y: hy + 68, w: 50, h: 14 },
    betBtn2:      { x: cx, y: hy + 68, w: 50, h: 14 },
    betBtn3:      { x: 156, y: hy + 68, w: 50, h: 14 },
    soundToggle:  { x: PORTRAIT_W - 6, y: hy + 4 },
    spectator:    { x: cx, y: hy + 4 },
  };
}
